/**
 * @file api.ts
 * @brief TanStack Query hooks for the Super Admin → Companies feature.
 *
 * @details
 * - Centralizes every query/mutation against `/api/admin/companies/*`.
 * - All inputs validated client-side before transmission; all responses
 *   parsed against Zod schemas before being handed to React.
 * - `useCompanyOptions` is a tiny convenience hook used by the
 *   `<CompanyPicker>` (and any future "select a company" surface) — it
 *   calls the same list endpoint with a tiny page size.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/client       (apiGet/Post/Put/Delete)
 * - shared/api/endpoints    (URL registry)
 * - features/companies/schemas
 *
 * @references
 * - BACKEND/src/features/admin/routes.js
 *
 * @author DES
 * @date 26-05-2026
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { z } from "zod";
import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
} from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import {
  AdminCompaniesListResponseSchema,
  AdminCompanySchema,
  type AdminCompaniesListParams,
  type AdminCompaniesListResponse,
  type AdminCompany,
  type CreateCompanyInput,
  type UpdateCompanyInput,
} from "./schemas";
import {
  EnterpriseStatusSchema,
  type EnterpriseStatus,
  type UpsertEnterpriseInput,
} from "./enterpriseSchemas";

// ----------------------------------------------------------------------
// Query keys
// ----------------------------------------------------------------------
export const companiesKeys = {
  all: ["admin", "companies"] as const,
  list: (params: AdminCompaniesListParams) =>
    ["admin", "companies", "list", params] as const,
  detail: (id: string) => ["admin", "companies", "detail", id] as const,
  options: (search: string) =>
    ["admin", "companies", "options", search] as const,
  enterprise: (id: string) =>
    ["admin", "companies", "enterprise", id] as const,
};

// ----------------------------------------------------------------------
// useCompaniesList — paginated, filterable list
// ----------------------------------------------------------------------
function buildListSearchParams(params: AdminCompaniesListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set("page", String(params.page));
  if (params.limit) usp.set("limit", String(params.limit));
  if (params.status) usp.set("status", params.status);
  if (params.search) usp.set("search", params.search);
  const s = usp.toString();
  return s ? `?${s}` : "";
}

async function fetchCompaniesPage(
  params: AdminCompaniesListParams,
): Promise<AdminCompaniesListResponse> {
  const url = `${adminEndpoints.companies}${buildListSearchParams(params)}`;
  const raw = await apiGet<unknown>(url);
  return AdminCompaniesListResponseSchema.parse(raw);
}

export function useCompaniesList(
  params: AdminCompaniesListParams,
  options?: Partial<UseQueryOptions<AdminCompaniesListResponse>>,
) {
  return useQuery<AdminCompaniesListResponse>({
    queryKey: companiesKeys.list(params),
    queryFn: () => fetchCompaniesPage(params),
    placeholderData: (prev) => prev,
    ...options,
  });
}

/** One status in the grouped list (`?group=status`, DES 01-10-2026). */
export const CompaniesStatusGroupSchema = z.object({
  status: z.string().nullable(),
  count: z.number(),
});
export type CompaniesStatusGroup = z.infer<typeof CompaniesStatusGroupSchema>;

/** The group key for a status; `none` for companies with no status. */
export const companiesStatusGroupKey = (status: string | null) => status || "none";

/** The list filters without paging (shared by the grouped calls). */
function filterQuery(params: AdminCompaniesListParams): URLSearchParams {
  return new URLSearchParams(
    buildListSearchParams({ ...params, page: undefined, limit: undefined }).replace(/^\?/, ""),
  );
}

/**
 * Every company status under the current filters, with counts (DES
 * 01-10-2026: "haan superadmin pages pe bhi yehi kar do"), as on company
 * Invoices. One server GROUP BY instead of loading every company.
 */
export function useCompaniesGroupSummary(
  params: AdminCompaniesListParams,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...companiesKeys.all, "groups", filterQuery(params).toString()],
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const usp = filterQuery(params);
      usp.set("group", "status");
      const raw = await apiGet<{ groups?: unknown }>(`${adminEndpoints.companies}?${usp}`);
      return z.array(CompaniesStatusGroupSchema).parse(raw.groups ?? []);
    },
  });
}

/** Rows per batch when a group opens or loads more. */
export const COMPANY_GROUP_BATCH = 25;

/**
 * The companies of each OPEN status group, `batches` x COMPANY_GROUP_BATCH,
 * under the same filters (`?status=`). The route does not cap `limit`, so
 * one request asks for every loaded batch.
 */
export function useCompaniesGroupRows(
  params: AdminCompaniesListParams,
  open: { key: string; batches: number }[],
) {
  return useQueries({
    queries: open.map(({ key, batches }) => ({
      queryKey: [...companiesKeys.all, "group", key, batches, filterQuery(params).toString()],
      placeholderData: (prev: AdminCompaniesListResponse | undefined) => prev,
      queryFn: async (): Promise<AdminCompaniesListResponse> => {
        const usp = filterQuery(params);
        usp.set("status", key);
        usp.set("page", "1");
        usp.set("limit", String(batches * COMPANY_GROUP_BATCH));
        const raw = await apiGet<unknown>(`${adminEndpoints.companies}?${usp}`);
        return AdminCompaniesListResponseSchema.parse(raw);
      },
    })),
  });
}

// ----------------------------------------------------------------------
// useCompany — single company by id (detail page / picker resolve)
// ----------------------------------------------------------------------
export function useCompany(id: string | null, enabled = true) {
  return useQuery<AdminCompany>({
    queryKey: companiesKeys.detail(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(adminEndpoints.companyById(id!));
      return AdminCompanySchema.parse(raw);
    },
  });
}

// ----------------------------------------------------------------------
// useCompanyOptions — lightweight list hook for pickers / autocomplete.
// Returns up to 25 companies matching the search term.
// ----------------------------------------------------------------------
export function useCompanyOptions(search: string, enabled = true) {
  const trimmed = search.trim();
  return useQuery<AdminCompaniesListResponse>({
    queryKey: companiesKeys.options(trimmed),
    enabled,
    queryFn: async () => {
      const usp = new URLSearchParams();
      usp.set("limit", "25");
      if (trimmed) usp.set("search", trimmed);
      const url = `${adminEndpoints.companies}?${usp.toString()}`;
      const raw = await apiGet<unknown>(url);
      return AdminCompaniesListResponseSchema.parse(raw);
    },
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

// ----------------------------------------------------------------------
// Mutations
// ----------------------------------------------------------------------
export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation<AdminCompany, Error, CreateCompanyInput>({
    mutationFn: async (input) => {
      const raw = await apiPost<unknown>(adminEndpoints.companies, input);
      return AdminCompanySchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companiesKeys.all });
    },
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation<
    AdminCompany,
    Error,
    { id: string; input: UpdateCompanyInput }
  >({
    mutationFn: async ({ id, input }) => {
      const raw = await apiPut<unknown>(adminEndpoints.companyById(id), input);
      return AdminCompanySchema.parse(raw);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: companiesKeys.all });
      void qc.invalidateQueries({ queryKey: companiesKeys.detail(vars.id) });
    },
  });
}

export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: async (id) =>
      apiDelete<unknown>(adminEndpoints.companyById(id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companiesKeys.all });
    },
  });
}

export function useSuspendCompany() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: async (id) =>
      apiPost<unknown>(adminEndpoints.companySuspend(id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companiesKeys.all });
    },
  });
}

export function useReactivateCompany() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: async (id) =>
      apiPost<unknown>(adminEndpoints.companyReactivate(id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companiesKeys.all });
    },
  });
}

export function useCompanyEnterprise(companyId: string | null, enabled = true) {
  return useQuery<EnterpriseStatus>({
    queryKey: companiesKeys.enterprise(companyId ?? ""),
    enabled: enabled && Boolean(companyId),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        adminEndpoints.companyEnterprise(companyId!),
      );
      return EnterpriseStatusSchema.parse(raw);
    },
  });
}

export function useUpsertCompanyEnterprise(companyId: string) {
  const qc = useQueryClient();
  return useMutation<EnterpriseStatus, Error, UpsertEnterpriseInput>({
    mutationFn: async (input) => {
      const raw = await apiPut<unknown>(
        adminEndpoints.companyEnterprise(companyId),
        input,
      );
      return EnterpriseStatusSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: companiesKeys.enterprise(companyId),
      });
      void qc.invalidateQueries({ queryKey: companiesKeys.all });
      void qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useSetCompanyEnterpriseStatus(companyId: string) {
  const qc = useQueryClient();
  return useMutation<EnterpriseStatus, Error, string>({
    mutationFn: async (status) => {
      const raw = await apiPost<unknown>(
        adminEndpoints.companyEnterpriseStatus(companyId),
        { status },
      );
      return EnterpriseStatusSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: companiesKeys.enterprise(companyId),
      });
      void qc.invalidateQueries({ queryKey: companiesKeys.all });
    },
  });
}

export function useAssignCompanyEnterpriseSeat(companyId: string) {
  const qc = useQueryClient();
  return useMutation<EnterpriseStatus, Error, string>({
    mutationFn: async (userId) => {
      const raw = await apiPost<unknown>(
        adminEndpoints.companyEnterpriseSeats(companyId),
        { userId },
      );
      return EnterpriseStatusSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: companiesKeys.enterprise(companyId),
      });
      void qc.invalidateQueries({ queryKey: companiesKeys.all });
      void qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useRevokeCompanyEnterpriseSeat(companyId: string) {
  const qc = useQueryClient();
  return useMutation<EnterpriseStatus, Error, string>({
    mutationFn: async (userId) => {
      const raw = await apiDelete<unknown>(
        adminEndpoints.companyEnterpriseSeatByUser(companyId, userId),
      );
      return EnterpriseStatusSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: companiesKeys.enterprise(companyId),
      });
      void qc.invalidateQueries({ queryKey: companiesKeys.all });
      void qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}
